/**
 * BATCH 23 PART 5 — Workflows 71-88: Content & Social
 * 18 content and social media workflow templates
 * Services: WordPress, LinkedIn API, YouTube API, Airtable, Discourse, Reddit API, Asana, Google Drive, Buffer, OpenAI, Slack, Google Sheets
 */

// ============ HELPER FUNCTIONS ============

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// ============ WORKFLOWS 71-88 ============

export const ELITE_BATCH23_PART5: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ── 71. AI Blog Post Draft + Editor Review | Content | Pattern I ──
  "AI Blog Post Draft + Editor Review": {
    nodes: [
      trigger("Webhook: Content brief submitted", "webhook_form", {
        source: "ContentTeam",
        event: "brief.submitted",
        howItStarts:
          "Fires when a writer or content strategist submits a blog post brief through the intake form.",
        setup:
          "Connect the content brief intake form webhook. The payload includes topic, target keywords, audience, and desired word count.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Research topic and gather key points",
        240,
        "You are a content research assistant. Analyze the provided blog brief and return JSON with topic_title, primary_keyword, secondary_keywords (comma-separated), target_audience, key_points (5 bullet points as a single string), and recommended_word_count.",
        "Blog brief submitted: {{{webhook_body}}}",
        [
          { field: "topic_title", as: "topic_title", type: "string" },
          { field: "primary_keyword", as: "primary_keyword", type: "string" },
          {
            field: "secondary_keywords",
            as: "secondary_keywords",
            type: "string",
          },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "key_points", as: "key_points", type: "string" },
          {
            field: "recommended_word_count",
            as: "recommended_word_count",
            type: "number",
          },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Draft full blog post from research",
        480,
        "You are an expert blog writer. Given the research output, draft a complete blog post. Return JSON with draft_title, draft_body (full HTML blog post), meta_description (under 160 chars), and estimated_read_time.",
        "Topic: {topic_title}. Primary keyword: {primary_keyword}. Secondary: {secondary_keywords}. Audience: {target_audience}. Key points: {key_points}. Target word count: {recommended_word_count}.",
        [
          { field: "draft_title", as: "draft_title", type: "string" },
          { field: "draft_body", as: "draft_body", type: "string" },
          { field: "meta_description", as: "meta_description", type: "string" },
          {
            field: "estimated_read_time",
            as: "estimated_read_time",
            type: "string",
          },
        ],
      ),
      n("4", "runCode", "SEO checks: validate keyword density and meta", 720, {
        runCodeDetails: {
          code: "const body = input.draft_body || ''; const keyword = input.primary_keyword || ''; const wordCount = body.split(/\\s+/).length; const keywordCount = (body.match(new RegExp(keyword, 'gi')) || []).length; const density = ((keywordCount / wordCount) * 100).toFixed(2); const metaLen = (input.meta_description || '').length; const seoPass = parseFloat(density) >= 0.5 && parseFloat(density) <= 3.0 && metaLen >= 120 && metaLen <= 160; return { seo_pass: seoPass, keyword_density: density, word_count: wordCount, meta_length: metaLen };",
          outputVariables: [
            { name: "seo_pass", type: "boolean" },
            { name: "keyword_density", type: "string" },
            { name: "word_count", type: "number" },
            { name: "meta_length", type: "number" },
          ],
        },
      }),
      n("5", "approvalGate", "Editor: Review draft and SEO report", 960, {
        approvalGateDetails: {
          approvers: ["content-editor"],
          message:
            'Blog draft ready for review: "{draft_title}" — {word_count} words, keyword density {keyword_density}%, meta {meta_length} chars. SEO pass: {seo_pass}. Please review content quality, brand voice, and factual accuracy.',
        },
      }),
      n("6", "hitApi", "WordPress: Publish approved blog post", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://{wp_domain}/wp-json/wp/v2/posts",
          headers: [
            { key: "Authorization", value: "Bearer {wp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "title", value: "{draft_title}" },
            { key: "content", value: "{draft_body}" },
            { key: "status", value: "publish" },
            { key: "meta", value: '{"description":"{meta_description}"}' },
          ],
          variables: [
            { name: "id", customName: "wp_post_id", type: "number" },
            { name: "link", customName: "wp_post_url", type: "string" },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Notify content team of publication",
        1440,
        'New blog published: "{draft_title}" ({estimated_read_time} read)\\nURL: {wp_post_url}\\nKeyword: {primary_keyword} | Density: {keyword_density}% | Words: {word_count}\\nAudience: {target_audience}',
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log published blog post",
        1680,
        "BlogPublications",
        '[["{draft_title}","{primary_keyword}","{keyword_density}","{word_count}","{wp_post_url}","{target_audience}","{estimated_read_time}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "AI blog post creation pipeline: brief submission triggers AI topic research, AI drafts a full blog post, automated SEO checks validate keyword density and meta description, editor approval gate ensures quality, then WordPress publishes the post with Slack notification and Sheets logging.",
  },

  // ── 72. Newsletter to LinkedIn Thread Converter | Content | Pattern I ──
  "Newsletter to LinkedIn Thread Converter": {
    nodes: [
      trigger("Mailchimp: Newsletter sent webhook", "webhook_app", {
        source: "Mailchimp",
        event: "campaign.sent",
        howItStarts:
          "Fires when a newsletter campaign is successfully sent through Mailchimp.",
        setup:
          "Connect Mailchimp webhook for the campaign.sent event. Ensure the full campaign HTML body and subject line are included in the payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract key points from newsletter",
        240,
        "You are a content strategist. Analyze the newsletter HTML and extract the most shareable insights. Return JSON with newsletter_subject, key_points (array of 5-7 bullet-point strings joined by |), main_theme, target_audience, and newsletter_url.",
        "Newsletter campaign sent payload: {{{webhook_body}}}",
        [
          {
            field: "newsletter_subject",
            as: "newsletter_subject",
            type: "string",
          },
          { field: "key_points", as: "key_points", type: "string" },
          { field: "main_theme", as: "main_theme", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          { field: "newsletter_url", as: "newsletter_url", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Format as LinkedIn thread posts",
        480,
        "You are a LinkedIn content expert. Convert the key points into a LinkedIn thread format. Return JSON with thread_hook (compelling opening post under 300 chars), thread_posts (array of 3-5 posts joined by |||), thread_cta (closing post with call-to-action), and total_post_count.",
        "Subject: {newsletter_subject}. Theme: {main_theme}. Key points: {key_points}. Audience: {target_audience}. Newsletter link: {newsletter_url}.",
        [
          { field: "thread_hook", as: "thread_hook", type: "string" },
          { field: "thread_posts", as: "thread_posts", type: "string" },
          { field: "thread_cta", as: "thread_cta", type: "string" },
          { field: "total_post_count", as: "total_post_count", type: "number" },
        ],
      ),
      n("4", "runCode", "Validate character limits for LinkedIn", 720, {
        runCodeDetails: {
          code: "const hook = input.thread_hook || ''; const posts = (input.thread_posts || '').split('|||'); const cta = input.thread_cta || ''; const hookOk = hook.length <= 3000; const postsOk = posts.every(p => p.trim().length <= 3000); const ctaOk = cta.length <= 3000; const allPass = hookOk && postsOk && ctaOk; return { char_check_pass: allPass, hook_length: hook.length, post_count: posts.length, cta_length: cta.length };",
          outputVariables: [
            { name: "char_check_pass", type: "boolean" },
            { name: "hook_length", type: "number" },
            { name: "post_count", type: "number" },
            { name: "cta_length", type: "number" },
          ],
        },
      }),
      n("5", "approvalGate", "Brand manager: Approve LinkedIn thread", 960, {
        approvalGateDetails: {
          approvers: ["brand-manager"],
          message:
            'LinkedIn thread from newsletter "{newsletter_subject}" ready for review.\\nHook ({hook_length} chars): {thread_hook}\\n{post_count} thread posts + CTA ({cta_length} chars).\\nAll char checks passed: {char_check_pass}',
        },
      }),
      n("6", "hitApi", "LinkedIn API: Publish thread as post", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.linkedin.com/v2/ugcPosts",
          headers: [
            { key: "Authorization", value: "Bearer {linkedin_token}" },
            { key: "Content-Type", value: "application/json" },
            { key: "X-Restli-Protocol-Version", value: "2.0.0" },
          ],
          body: [
            { key: "author", value: "urn:li:organization:{linkedin_org_id}" },
            { key: "lifecycleState", value: "PUBLISHED" },
            {
              key: "specificContent",
              value:
                '{"com.linkedin.ugc.ShareContent":{"shareCommentary":{"text":"{thread_hook}\\n\\n{thread_posts}\\n\\n{thread_cta}"},"shareMediaCategory":"NONE"}}',
            },
            {
              key: "visibility",
              value: '{"com.linkedin.ugc.MemberNetworkVisibility":"PUBLIC"}',
            },
          ],
          variables: [
            { name: "id", customName: "linkedin_post_id", type: "string" },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Confirm LinkedIn thread published",
        1440,
        'LinkedIn thread published from newsletter "{newsletter_subject}"\\nTheme: {main_theme} | Posts: {total_post_count}\\nPost ID: {linkedin_post_id}\\nNewsletter link: {newsletter_url}',
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log newsletter-to-LinkedIn repurpose",
        1680,
        "ContentRepurposing",
        '[["{newsletter_subject}","{main_theme}","LinkedIn Thread","{total_post_count}","{linkedin_post_id}","{newsletter_url}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Newsletter-to-LinkedIn repurposing: Mailchimp campaign sent triggers AI extraction of key points, AI formats them into a LinkedIn thread with hook and CTA, character limits are validated, brand manager approves, then LinkedIn API publishes the thread with Slack confirmation and Sheets logging.",
  },

  // ── 73. YouTube Upload to Newsletter Summary | Content | Pattern I ──
  "YouTube Upload to Newsletter Summary": {
    nodes: [
      trigger("YouTube: New video published webhook", "webhook_app", {
        source: "YouTube",
        event: "video.published",
        howItStarts:
          "Fires when a new video is published on the connected YouTube channel.",
        setup:
          "Connect YouTube Data API webhook (PubSubHubbub) for new video publish events. Ensure video ID and channel info are included.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "YouTube API: Fetch video transcript and metadata",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://www.googleapis.com/youtube/v3/videos?part=snippet,contentDetails,statistics&id={video_id}&key={youtube_api_key}",
            headers: [{ key: "Accept", value: "application/json" }],
            body: [],
            variables: [
              {
                name: "items[0].snippet.title",
                customName: "video_title",
                type: "string",
              },
              {
                name: "items[0].snippet.description",
                customName: "video_description",
                type: "string",
              },
              {
                name: "items[0].contentDetails.duration",
                customName: "video_duration",
                type: "string",
              },
              {
                name: "items[0].snippet.thumbnails.high.url",
                customName: "thumbnail_url",
                type: "string",
              },
              {
                name: "items[0].snippet.channelTitle",
                customName: "channel_name",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Summarize video for newsletter audience",
        480,
        "You are a newsletter content writer. Summarize the YouTube video for an email newsletter audience. Return JSON with email_subject (compelling subject line), intro_paragraph (2-3 sentences hooking the reader), key_takeaways (5 bullet points joined by |), and cta_text (call-to-action to watch).",
        "Video title: {video_title}. Description: {video_description}. Duration: {video_duration}. Channel: {channel_name}.",
        [
          { field: "email_subject", as: "email_subject", type: "string" },
          { field: "intro_paragraph", as: "intro_paragraph", type: "string" },
          { field: "key_takeaways", as: "key_takeaways", type: "string" },
          { field: "cta_text", as: "cta_text", type: "string" },
        ],
      ),
      n("4", "runCode", "Format newsletter email HTML blocks", 720, {
        runCodeDetails: {
          code: "const takeaways = (input.key_takeaways || '').split('|').map(t => '<li>' + t.trim() + '</li>').join(''); const emailHtml = '<h2>' + input.email_subject + '</h2><p>' + input.intro_paragraph + '</p><img src=\"' + input.thumbnail_url + '\" alt=\"' + input.video_title + '\" /><h3>Key Takeaways</h3><ul>' + takeaways + '</ul><p><a href=\"https://youtube.com/watch?v=' + input.video_id + '\">' + input.cta_text + '</a></p>'; return { email_html: emailHtml, takeaway_count: (input.key_takeaways || '').split('|').length, has_thumbnail: !!input.thumbnail_url };",
          outputVariables: [
            { name: "email_html", type: "string" },
            { name: "takeaway_count", type: "number" },
            { name: "has_thumbnail", type: "boolean" },
          ],
        },
      }),
      n("5", "approvalGate", "Editor: Review newsletter summary draft", 960, {
        approvalGateDetails: {
          approvers: ["newsletter-editor"],
          message:
            'Newsletter summary for YouTube video "{video_title}" is ready.\\nSubject: {email_subject}\\nTakeaways: {takeaway_count} | Thumbnail: {has_thumbnail}\\nPlease review tone, accuracy, and formatting before sending.',
        },
      }),
      n("6", "hitApi", "SendGrid: Send newsletter to subscriber list", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.sendgrid.com/v3/mail/send",
          headers: [
            { key: "Authorization", value: "Bearer {sendgrid_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "personalizations",
              value: '[{"to":[{"email":"{newsletter_list_email}"}]}]',
            },
            {
              key: "from",
              value: '{"email":"{from_email}","name":"{channel_name}"}',
            },
            { key: "subject", value: "{email_subject}" },
            {
              key: "content",
              value: '[{"type":"text/html","value":"{email_html}"}]',
            },
          ],
          variables: [
            { name: "statusCode", customName: "send_status", type: "number" },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Notify team of newsletter sent",
        1440,
        'Newsletter sent for YouTube video "{video_title}"\\nSubject: {email_subject}\\nDuration: {video_duration} | Takeaways: {takeaway_count}\\nSend status: {send_status}',
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log video-to-newsletter conversion",
        1680,
        "VideoNewsletters",
        '[["{video_title}","{video_duration}","{email_subject}","{takeaway_count}","{send_status}","{channel_name}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "YouTube-to-newsletter pipeline: new video publish triggers transcript/metadata fetch from YouTube API, AI summarizes content for newsletter readers, HTML blocks are formatted, editor approves, SendGrid sends the newsletter, with Slack notification and Sheets logging.",
  },

  // ── 74. Long Video to Shorts Clips Queue | Content | Pattern I ──
  "Long Video to Shorts Clips Queue": {
    nodes: [
      trigger("Webhook: Long-form video uploaded", "webhook_app", {
        source: "VideoTeam",
        event: "video.uploaded",
        howItStarts:
          "Fires when a long-form video file is uploaded to the content pipeline for clip extraction.",
        setup:
          "Connect the video upload webhook. Payload should include video URL, transcript text, and video duration in seconds.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Analyze transcript for highlight moments",
        240,
        "You are a video editor assistant. Analyze the transcript and identify the 5 most engaging/shareable moments suitable for short-form clips (30-60 seconds). Return JSON with video_title, total_duration_seconds, highlights (array of 5 objects with start_time, end_time, topic, hook_line joined by |||), and highlight_count.",
        "Video transcript and metadata: {{{webhook_body}}}",
        [
          { field: "video_title", as: "video_title", type: "string" },
          {
            field: "total_duration_seconds",
            as: "total_duration_seconds",
            type: "number",
          },
          { field: "highlights", as: "highlights", type: "string" },
          { field: "highlight_count", as: "highlight_count", type: "number" },
        ],
      ),
      n(
        "3",
        "runCode",
        "Identify clip timestamps and validate durations",
        480,
        {
          runCodeDetails: {
            code: "const clips = (input.highlights || '').split('|||'); const parsed = clips.map((c, i) => { const parts = c.split('|'); return { index: i+1, start: parts[0]||'0:00', end: parts[1]||'1:00', topic: parts[2]||'Highlight', hook: parts[3]||'' }; }); const validClips = parsed.filter(c => true); return { clip_count: validClips.length, clip_summary: validClips.map(c => c.index + ': ' + c.start + '-' + c.end + ' ' + c.topic).join('; '), clips_json: JSON.stringify(validClips) };",
            outputVariables: [
              { name: "clip_count", type: "number" },
              { name: "clip_summary", type: "string" },
              { name: "clips_json", type: "string" },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "OpenAI: Generate clip titles and hooks for each segment",
        720,
        "You are a social media content expert. For each video clip segment, generate an attention-grabbing short-form title (under 60 chars) and a hook line for the first 3 seconds. Return JSON with clip_titles (titles joined by |||), clip_hooks (hooks joined by |||), and best_clip_index (the single most viral-worthy clip number).",
        "Video: {video_title}. Clips: {clips_json}.",
        [
          { field: "clip_titles", as: "clip_titles", type: "string" },
          { field: "clip_hooks", as: "clip_hooks", type: "string" },
          { field: "best_clip_index", as: "best_clip_index", type: "number" },
        ],
      ),
      n("5", "approvalGate", "Creative lead: Review clip selections", 960, {
        approvalGateDetails: {
          approvers: ["creative-lead"],
          message:
            'Clip queue ready for "{video_title}" ({total_duration_seconds}s source video).\\n{clip_count} clips identified:\\n{clip_summary}\\nBest viral candidate: clip #{best_clip_index}\\nPlease approve or adjust clip selections.',
        },
      }),
      n("6", "hitApi", "Airtable: Create clip queue records", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.airtable.com/v0/{airtable_base_id}/ClipQueue",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "records",
              value:
                '[{"fields":{"Source Video":"{video_title}","Clip Titles":"{clip_titles}","Clip Hooks":"{clip_hooks}","Clip Count":{clip_count},"Best Clip":"{best_clip_index}","Status":"Queued"}}]',
            },
          ],
          variables: [
            {
              name: "records[0].id",
              customName: "airtable_record_id",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Assign clip editing to video team",
        1440,
        'Clip queue created for "{video_title}"\\n{clip_count} clips to edit | Best clip: #{best_clip_index}\\nTimestamps: {clip_summary}\\nAirtable record: {airtable_record_id}\\nPlease begin editing in priority order.',
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log clip queue batch",
        1680,
        "ClipQueue",
        '[["{video_title}","{clip_count}","{best_clip_index}","{clip_summary}","{airtable_record_id}","Queued","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Long video to shorts pipeline: video upload triggers AI transcript analysis to find highlight moments, timestamps are validated, AI generates clip titles and hooks, creative lead approves selections, Airtable clip queue is created, Slack assigns editing tasks, and Sheets logs the batch.",
  },

  // ── 75. Podcast Transcript to Show Notes | Content | Pattern I ──
  "Podcast Transcript to Show Notes": {
    nodes: [
      trigger("Buzzsprout: New episode published webhook", "webhook_app", {
        source: "Buzzsprout",
        event: "episode.published",
        howItStarts:
          "Fires when a new podcast episode is published on Buzzsprout.",
        setup:
          "Connect Buzzsprout webhook for episode publish events. Ensure episode title, audio URL, and transcript are included in the payload.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Transcribe and extract episode details",
        240,
        "You are a podcast content assistant. Process the Buzzsprout episode payload and extract episode details. Return JSON with episode_title, episode_number, guest_name (or 'Solo' if no guest), audio_url, transcript_text, and episode_duration.",
        "Buzzsprout episode payload: {{{webhook_body}}}",
        [
          { field: "episode_title", as: "episode_title", type: "string" },
          { field: "episode_number", as: "episode_number", type: "string" },
          { field: "guest_name", as: "guest_name", type: "string" },
          { field: "audio_url", as: "audio_url", type: "string" },
          { field: "transcript_text", as: "transcript_text", type: "string" },
          { field: "episode_duration", as: "episode_duration", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Generate show notes with timestamps",
        480,
        "You are a podcast show notes writer. Create detailed show notes from the transcript. Return JSON with show_notes_html (formatted with timestamps, topics, and links), key_quotes (3 notable quotes joined by |), topics_covered (comma-separated), and seo_description (under 160 chars for CMS meta).",
        "Episode: {episode_title} (#{episode_number}). Guest: {guest_name}. Duration: {episode_duration}. Transcript: {transcript_text}.",
        [
          { field: "show_notes_html", as: "show_notes_html", type: "string" },
          { field: "key_quotes", as: "key_quotes", type: "string" },
          { field: "topics_covered", as: "topics_covered", type: "string" },
          { field: "seo_description", as: "seo_description", type: "string" },
        ],
      ),
      n("4", "runCode", "Format show notes for WordPress CMS", 720, {
        runCodeDetails: {
          code: "const quotes = (input.key_quotes || '').split('|'); const quoteHtml = quotes.map(q => '<blockquote>' + q.trim() + '</blockquote>').join(''); const fullHtml = '<h2>Episode #' + input.episode_number + ': ' + input.episode_title + '</h2>' + (input.guest_name !== 'Solo' ? '<p><strong>Guest:</strong> ' + input.guest_name + '</p>' : '') + '<p><strong>Duration:</strong> ' + input.episode_duration + '</p><audio controls src=\"' + input.audio_url + '\"></audio>' + input.show_notes_html + '<h3>Notable Quotes</h3>' + quoteHtml; return { cms_html: fullHtml, quote_count: quotes.length, topics: input.topics_covered };",
          outputVariables: [
            { name: "cms_html", type: "string" },
            { name: "quote_count", type: "number" },
            { name: "topics", type: "string" },
          ],
        },
      }),
      n("5", "approvalGate", "Podcast host: Review show notes", 960, {
        approvalGateDetails: {
          approvers: ["podcast-host"],
          message:
            'Show notes ready for Episode #{episode_number}: "{episode_title}"\\nGuest: {guest_name} | Duration: {episode_duration}\\nTopics: {topics}\\n{quote_count} notable quotes extracted.\\nPlease review accuracy and approve for publication.',
        },
      }),
      n("6", "hitApi", "WordPress: Publish show notes page", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://{wp_domain}/wp-json/wp/v2/posts",
          headers: [
            { key: "Authorization", value: "Bearer {wp_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "title",
              value: "Episode #{episode_number}: {episode_title}",
            },
            { key: "content", value: "{cms_html}" },
            { key: "status", value: "publish" },
            { key: "categories", value: "[{podcast_category_id}]" },
            { key: "meta", value: '{"description":"{seo_description}"}' },
          ],
          variables: [
            { name: "id", customName: "wp_page_id", type: "number" },
            { name: "link", customName: "show_notes_url", type: "string" },
          ],
        },
      }),
      emailNode(
        "7",
        "Email: Send guest their show notes link",
        1440,
        "{guest_email}",
        "Your episode is live! Episode #{episode_number}: {episode_title}",
        "Hi {guest_name},\\n\\nYour podcast episode is now live with full show notes!\\n\\nEpisode: #{episode_number} — {episode_title}\\nDuration: {episode_duration}\\nShow notes: {show_notes_url}\\n\\nKey quotes we featured:\\n{key_quotes}\\n\\nTopics covered: {topics_covered}\\n\\nFeel free to share with your audience. Thank you for being a fantastic guest!",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log episode publication",
        1680,
        "PodcastEpisodes",
        '[["{episode_number}","{episode_title}","{guest_name}","{episode_duration}","{show_notes_url}","{topics_covered}","{quote_count}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Podcast show notes automation: Buzzsprout publish triggers AI transcript extraction, AI generates timestamped show notes and key quotes, content is formatted for WordPress, host approves, WordPress publishes, guest receives email with their episode link, and Sheets logs the publication.",
  },

  // ── 76. Meeting Recording to Knowledge Article | Content | Pattern I ──
  "Meeting Recording to Knowledge Article": {
    nodes: [
      trigger("Zoom: Meeting recording ready webhook", "webhook_app", {
        source: "Zoom",
        event: "recording.completed",
        howItStarts:
          "Fires when a Zoom meeting recording is processed and ready for download.",
        setup:
          "Connect Zoom webhook for recording.completed events. Ensure meeting topic, participants, and transcript/recording URL are included.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Transcribe and extract meeting content",
        240,
        "You are a knowledge management assistant. Analyze the Zoom meeting recording payload and extract key content. Return JSON with meeting_topic, meeting_date, participant_count, host_name, transcript_summary, and recording_url.",
        "Zoom recording payload: {{{webhook_body}}}",
        [
          { field: "meeting_topic", as: "meeting_topic", type: "string" },
          { field: "meeting_date", as: "meeting_date", type: "string" },
          {
            field: "participant_count",
            as: "participant_count",
            type: "number",
          },
          { field: "host_name", as: "host_name", type: "string" },
          {
            field: "transcript_summary",
            as: "transcript_summary",
            type: "string",
          },
          { field: "recording_url", as: "recording_url", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Extract reusable knowledge for KB article",
        480,
        "You are a technical writer. From the meeting transcript summary, extract reusable knowledge suitable for a knowledge base article. Return JSON with article_title, article_body (well-structured HTML with headers and sections), decisions_made (joined by |), action_items (joined by |), and knowledge_category.",
        "Meeting: {meeting_topic} on {meeting_date}. Host: {host_name}. Participants: {participant_count}. Summary: {transcript_summary}.",
        [
          { field: "article_title", as: "article_title", type: "string" },
          { field: "article_body", as: "article_body", type: "string" },
          { field: "decisions_made", as: "decisions_made", type: "string" },
          { field: "action_items", as: "action_items", type: "string" },
          {
            field: "knowledge_category",
            as: "knowledge_category",
            type: "string",
          },
        ],
      ),
      n("4", "runCode", "Check for PII and sensitive content", 720, {
        runCodeDetails: {
          code: "const body = input.article_body || ''; const piiPatterns = [/\\b\\d{3}-\\d{2}-\\d{4}\\b/, /\\b\\d{16}\\b/, /\\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Z|a-z]{2,}\\b/]; const piiFound = piiPatterns.some(p => p.test(body)); const hasSensitiveKeywords = /confidential|secret|private salary|NDA/i.test(body); return { pii_detected: piiFound, sensitive_content: hasSensitiveKeywords, knowledge_worthy: !piiFound && !hasSensitiveKeywords, word_count: body.split(/\\s+/).length };",
          outputVariables: [
            { name: "pii_detected", type: "boolean" },
            { name: "sensitive_content", type: "boolean" },
            { name: "knowledge_worthy", type: "boolean" },
            { name: "word_count", type: "number" },
          ],
        },
      }),
      n("5", "condition", "Is the content safe and knowledge-worthy?", 960, {
        conditionDetails: {
          conditions: [
            { field: "knowledge_worthy", operator: "equals", value: "true" },
          ],
        },
      }),
      n("6", "hitApi", "Confluence: Create knowledge base article", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://{confluence_domain}/wiki/rest/api/content",
          headers: [
            { key: "Authorization", value: "Basic {confluence_auth}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "type", value: "page" },
            { key: "title", value: "{article_title}" },
            { key: "space", value: '{"key":"{confluence_space_key}"}' },
            {
              key: "body",
              value:
                '{"storage":{"value":"{article_body}","representation":"storage"}}',
            },
          ],
          variables: [
            { name: "id", customName: "confluence_page_id", type: "string" },
            {
              name: "_links.webui",
              customName: "confluence_url",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Notify team of new knowledge article",
        1440,
        'New knowledge article from meeting "{meeting_topic}"\\nTitle: {article_title} | Category: {knowledge_category}\\nDecisions: {decisions_made}\\nAction items: {action_items}\\nArticle: {confluence_url}',
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log knowledge extraction",
        1680,
        "KnowledgeArticles",
        '[["{meeting_topic}","{meeting_date}","{article_title}","{knowledge_category}","{word_count}","{confluence_url}","{host_name}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Meeting-to-knowledge pipeline: Zoom recording triggers AI transcription and content extraction, AI drafts a knowledge base article, PII/sensitive content checks run automatically, safe content gets published to Confluence, team is notified via Slack, and Sheets logs the extraction.",
  },

  // ── 77. User Review to Social Proof Asset | Content | Pattern J ──
  "User Review to Social Proof Asset": {
    nodes: [
      trigger("Webhook: 5-star review received", "webhook_app", {
        source: "ReviewPlatform",
        event: "review.created",
        howItStarts:
          "Fires when a 5-star customer review is submitted on G2, Trustpilot, or app store.",
        setup:
          "Connect review platform webhook for new review events filtered to 5-star ratings. Payload includes reviewer name, review text, rating, and platform.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract quotable text from review",
        240,
        "You are a social proof specialist. Extract the most compelling, quotable portion of this 5-star review. Return JSON with reviewer_name, review_platform, star_rating, full_review_text, quotable_excerpt (the single best sentence or phrase), product_mentioned, and use_case_theme.",
        "5-star review payload: {{{webhook_body}}}",
        [
          { field: "reviewer_name", as: "reviewer_name", type: "string" },
          { field: "review_platform", as: "review_platform", type: "string" },
          { field: "star_rating", as: "star_rating", type: "number" },
          { field: "quotable_excerpt", as: "quotable_excerpt", type: "string" },
          {
            field: "product_mentioned",
            as: "product_mentioned",
            type: "string",
          },
          { field: "use_case_theme", as: "use_case_theme", type: "string" },
        ],
      ),
      n("3", "runCode", "Check customer consent status for testimonials", 480, {
        runCodeDetails: {
          code: "const reviewer = input.reviewer_name || ''; const platform = input.review_platform || ''; const publicReview = ['G2', 'Trustpilot', 'Capterra', 'Google'].includes(platform); const consentGiven = publicReview; const initials = reviewer.split(' ').map(n => n[0]).join('.'); return { consent_given: consentGiven, display_name: consentGiven ? reviewer : initials, platform_is_public: publicReview, excerpt_length: (input.quotable_excerpt || '').length };",
          outputVariables: [
            { name: "consent_given", type: "boolean" },
            { name: "display_name", type: "string" },
            { name: "platform_is_public", type: "boolean" },
            { name: "excerpt_length", type: "number" },
          ],
        },
      }),
      n(
        "4",
        "condition",
        "Has customer given consent for testimonial use?",
        720,
        {
          conditionDetails: {
            conditions: [
              { field: "consent_given", operator: "equals", value: "true" },
            ],
          },
        },
      ),
      aiNode(
        "5",
        "OpenAI: Format as social proof asset",
        960,
        "You are a brand designer. Create a formatted social proof asset from this customer quote. Return JSON with social_card_text (formatted testimonial card text with quote, attribution, and stars), linkedin_post (ready-to-post LinkedIn testimonial share), and asset_category (e.g. 'Product Review', 'Use Case Story', 'ROI Testimonial').",
        'Quote: "{quotable_excerpt}" — {display_name} on {review_platform}. Product: {product_mentioned}. Theme: {use_case_theme}. Rating: {star_rating} stars.',
        [
          { field: "social_card_text", as: "social_card_text", type: "string" },
          { field: "linkedin_post", as: "linkedin_post", type: "string" },
          { field: "asset_category", as: "asset_category", type: "string" },
        ],
      ),
      n("6", "approvalGate", "Marketing: Approve social proof asset", 1200, {
        approvalGateDetails: {
          approvers: ["marketing-lead"],
          message:
            'Social proof asset from {display_name} ({review_platform}):\\nQuote: "{quotable_excerpt}"\\nCategory: {asset_category}\\nProduct: {product_mentioned}\\nPlease approve for library addition and social sharing.',
        },
      }),
      n("7", "hitApi", "Airtable: Add to social proof library", 1440, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.airtable.com/v0/{airtable_base_id}/SocialProofLibrary",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "records",
              value:
                '[{"fields":{"Reviewer":"{display_name}","Platform":"{review_platform}","Quote":"{quotable_excerpt}","Product":"{product_mentioned}","Category":"{asset_category}","Social Card":"{social_card_text}","LinkedIn Post":"{linkedin_post}","Status":"Approved"}}]',
            },
          ],
          variables: [
            {
              name: "records[0].id",
              customName: "airtable_proof_id",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "8",
        "Slack: Notify team of new social proof asset",
        1680,
        'New social proof asset approved!\\nReviewer: {display_name} ({review_platform})\\nQuote: "{quotable_excerpt}"\\nCategory: {asset_category} | Product: {product_mentioned}\\nAirtable: {airtable_proof_id}',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Review-to-social-proof pipeline: 5-star review triggers AI quote extraction, consent status is verified against the review platform, consented reviews get AI-formatted as social proof assets, marketing approves, Airtable stores the asset in the proof library, and Slack notifies the team.",
  },

  // ── 78. New Case Study Intake to Draft Creation | Content | Pattern I ──
  "New Case Study Intake to Draft Creation": {
    nodes: [
      trigger("Webhook: Case study request form submitted", "webhook_form", {
        source: "ContentTeam",
        event: "case_study.requested",
        howItStarts:
          "Fires when a customer success manager submits a case study request form with customer details and metrics.",
        setup:
          "Connect the case study intake form webhook. Payload includes customer name, industry, use case, key metrics, and CSM contact.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "HubSpot: Fetch customer account data and history",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://api.hubapi.com/crm/v3/objects/companies/search",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "filterGroups",
                value:
                  '[{"filters":[{"propertyName":"name","operator":"CONTAINS_TOKEN","value":"{customer_name}"}]}]',
              },
            ],
            variables: [
              {
                name: "results[0].properties.industry",
                customName: "customer_industry",
                type: "string",
              },
              {
                name: "results[0].properties.annualrevenue",
                customName: "customer_revenue",
                type: "string",
              },
              {
                name: "results[0].properties.num_associated_contacts",
                customName: "contact_count",
                type: "number",
              },
              {
                name: "results[0].properties.lifecyclestage",
                customName: "lifecycle_stage",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Draft case study outline from intake data",
        480,
        "You are a B2B case study writer. Create a compelling case study outline. Return JSON with case_study_title, challenge_section (2-3 paragraphs on the problem), solution_section (how the product was implemented), results_section (metrics and outcomes), pull_quote (a draft customer quote), and target_persona.",
        "Case study request: {{{webhook_body}}}. Customer industry: {customer_industry}. Revenue: {customer_revenue}. Lifecycle: {lifecycle_stage}.",
        [
          { field: "case_study_title", as: "case_study_title", type: "string" },
          {
            field: "challenge_section",
            as: "challenge_section",
            type: "string",
          },
          { field: "solution_section", as: "solution_section", type: "string" },
          { field: "results_section", as: "results_section", type: "string" },
          { field: "pull_quote", as: "pull_quote", type: "string" },
          { field: "target_persona", as: "target_persona", type: "string" },
        ],
      ),
      n("4", "runCode", "Attach metrics and format draft document", 720, {
        runCodeDetails: {
          code: "const sections = ['Challenge', 'Solution', 'Results']; const bodies = [input.challenge_section, input.solution_section, input.results_section]; const docBody = sections.map((s, i) => '<h2>' + s + '</h2><p>' + (bodies[i] || '') + '</p>').join('') + '<blockquote>' + (input.pull_quote || '') + '</blockquote>'; const wordCount = docBody.split(/\\s+/).length; return { draft_html: docBody, word_count: wordCount, has_metrics: /\\d+%|\\$\\d+|\\d+x/.test(input.results_section || ''), section_count: 3 };",
          outputVariables: [
            { name: "draft_html", type: "string" },
            { name: "word_count", type: "number" },
            { name: "has_metrics", type: "boolean" },
            { name: "section_count", type: "number" },
          ],
        },
      }),
      n("5", "approvalGate", "Customer Success: Review case study draft", 960, {
        approvalGateDetails: {
          approvers: ["customer-success-lead"],
          message:
            'Case study draft for "{case_study_title}" ready for review.\\nIndustry: {customer_industry} | Persona: {target_persona}\\nWord count: {word_count} | Has metrics: {has_metrics}\\nPull quote: "{pull_quote}"\\nPlease verify accuracy of customer details and metrics.',
        },
      }),
      n("6", "hitApi", "Google Docs: Create case study draft document", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://docs.googleapis.com/v1/documents",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [{ key: "title", value: "DRAFT: {case_study_title}" }],
          variables: [
            { name: "documentId", customName: "google_doc_id", type: "string" },
            {
              name: "documentId",
              customName: "google_doc_url",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Notify content writer of new case study",
        1440,
        'New case study draft created: "{case_study_title}"\\nIndustry: {customer_industry} | Persona: {target_persona}\\nWord count: {word_count} | Metrics included: {has_metrics}\\nGoogle Doc: https://docs.google.com/document/d/{google_doc_id}\\nPlease refine and prepare for customer approval.',
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log case study pipeline entry",
        1680,
        "CaseStudyPipeline",
        '[["{case_study_title}","{customer_industry}","{target_persona}","{word_count}","{has_metrics}","{google_doc_id}","Draft","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Case study creation pipeline: intake form triggers HubSpot customer data fetch, AI drafts a full case study with challenge/solution/results sections, metrics are attached and formatted, CS lead approves accuracy, Google Docs creates the draft, writer is notified via Slack, and Sheets tracks the pipeline.",
  },

  // ── 79. Customer Quote Approval Workflow | Content | Pattern J ──
  "Customer Quote Approval Workflow": {
    nodes: [
      trigger("Webhook: Customer quote submitted for use", "webhook_form", {
        source: "ContentTeam",
        event: "quote.submitted",
        howItStarts:
          "Fires when a team member submits a customer quote for potential use in marketing materials.",
        setup:
          "Connect the quote submission form webhook. Payload includes the quote text, customer name, company, context, and intended use.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract quote details and assess context",
        240,
        "You are a content compliance assistant. Analyze the submitted customer quote. Return JSON with customer_name, customer_company, quote_text, quote_context (where/when it was said), intended_use (how it will be used), sentiment_score (1-10), and is_attributable (boolean whether it can be publicly attributed).",
        "Quote submission payload: {{{webhook_body}}}",
        [
          { field: "customer_name", as: "customer_name", type: "string" },
          { field: "customer_company", as: "customer_company", type: "string" },
          { field: "quote_text", as: "quote_text", type: "string" },
          { field: "quote_context", as: "quote_context", type: "string" },
          { field: "intended_use", as: "intended_use", type: "string" },
          { field: "sentiment_score", as: "sentiment_score", type: "number" },
          { field: "is_attributable", as: "is_attributable", type: "boolean" },
        ],
      ),
      n("3", "runCode", "Check usage rights and prior consent records", 480, {
        runCodeDetails: {
          code: "const company = input.customer_company || ''; const hasNDA = false; const priorConsent = false; const quoteLen = (input.quote_text || '').length; const needsApproval = !priorConsent && input.is_attributable; return { needs_customer_approval: needsApproval, has_prior_consent: priorConsent, quote_length: quoteLen, usage_rights_clear: !hasNDA };",
          outputVariables: [
            { name: "needs_customer_approval", type: "boolean" },
            { name: "has_prior_consent", type: "boolean" },
            { name: "quote_length", type: "number" },
            { name: "usage_rights_clear", type: "boolean" },
          ],
        },
      }),
      n(
        "4",
        "approvalGate",
        "Customer approval: Request permission to use quote",
        720,
        {
          approvalGateDetails: {
            approvers: ["customer-contact"],
            message:
              'We\'d like to use the following quote from {customer_name} ({customer_company}):\\n"{quote_text}"\\nContext: {quote_context}\\nIntended use: {intended_use}\\nPlease approve or suggest edits.',
          },
        },
      ),
      n("5", "condition", "Was the quote approved by the customer?", 960, {
        conditionDetails: {
          conditions: [
            { field: "approval_status", operator: "equals", value: "approved" },
          ],
        },
      }),
      n(
        "6",
        "hitApi",
        "Airtable: Add approved quote to testimonial library",
        1200,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.airtable.com/v0/{airtable_base_id}/TestimonialLibrary",
            headers: [
              { key: "Authorization", value: "Bearer {airtable_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "records",
                value:
                  '[{"fields":{"Customer":"{customer_name}","Company":"{customer_company}","Quote":"{quote_text}","Context":"{quote_context}","Use":"{intended_use}","Sentiment":{sentiment_score},"Status":"Approved","Consent":"Customer Approved"}}]',
              },
            ],
            variables: [
              {
                name: "records[0].id",
                customName: "airtable_quote_id",
                type: "string",
              },
            ],
          },
        },
      ),
      slackNode(
        "7",
        "Slack: Notify marketing of approved quote",
        1440,
        'Customer quote approved for use!\\nCustomer: {customer_name} ({customer_company})\\nQuote: "{quote_text}"\\nIntended use: {intended_use} | Sentiment: {sentiment_score}/10\\nAirtable: {airtable_quote_id}',
      ),
      emailNode(
        "8",
        "Email: Send confirmation to customer",
        1680,
        "{customer_email}",
        "Thank you for approving your testimonial, {customer_name}",
        'Hi {customer_name},\\n\\nThank you for allowing us to share your feedback!\\n\\nYour quote: "{quote_text}"\\n\\nWe\'ll be using this in our {intended_use} materials. We truly appreciate your partnership with us at {customer_company}.\\n\\nIf you ever want to update or withdraw this testimonial, please let us know.',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Customer quote approval workflow: quote submission triggers AI extraction and context assessment, usage rights are checked, customer approval gate requests permission, approved quotes are stored in Airtable testimonial library, marketing is notified via Slack, and a confirmation email is sent to the customer.",
  },

  // ── 80. Social Comment Triage + Response Suggestions | Content | Pattern K ──
  "Social Comment Triage + Response Suggestions": {
    nodes: [
      trigger(
        "Webhook: New social comment or mention received",
        "webhook_app",
        {
          source: "SocialPlatform",
          event: "comment.created",
          howItStarts:
            "Fires when a new comment or brand mention appears on any connected social media platform.",
          setup:
            "Connect social media monitoring webhook for new comments and mentions across platforms. Payload includes comment text, author, platform, and post context.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Classify sentiment and intent of comment",
        240,
        "You are a social media analyst. Classify this social comment. Return JSON with comment_text, author_handle, platform, parent_post_topic, sentiment (positive/neutral/negative), intent (question/complaint/praise/feedback/spam), urgency_level (low/medium/high), and requires_response (boolean).",
        "Social comment payload: {{{webhook_body}}}",
        [
          { field: "comment_text", as: "comment_text", type: "string" },
          { field: "author_handle", as: "author_handle", type: "string" },
          { field: "platform", as: "platform", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "intent", as: "intent", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
          {
            field: "requires_response",
            as: "requires_response",
            type: "boolean",
          },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Generate brand-aligned response suggestion",
        480,
        "You are a social media community manager. Draft a response suggestion for this comment that matches brand voice. Return JSON with suggested_response (the draft reply), tone_used (friendly/professional/empathetic/celebratory), escalation_needed (boolean if this needs manager attention), and response_priority (1-5, 5=urgent).",
        'Comment from @{author_handle} on {platform}: "{comment_text}". Sentiment: {sentiment}. Intent: {intent}. Urgency: {urgency_level}.',
        [
          {
            field: "suggested_response",
            as: "suggested_response",
            type: "string",
          },
          { field: "tone_used", as: "tone_used", type: "string" },
          {
            field: "escalation_needed",
            as: "escalation_needed",
            type: "boolean",
          },
          {
            field: "response_priority",
            as: "response_priority",
            type: "number",
          },
        ],
      ),
      n("4", "runCode", "Check response against brand guidelines", 720, {
        runCodeDetails: {
          code: "const response = input.suggested_response || ''; const bannedWords = ['competitor_name', 'lawsuit', 'hack']; const hasBanned = bannedWords.some(w => response.toLowerCase().includes(w)); const tooLong = response.length > 280; const guidelinesPass = !hasBanned && !tooLong; return { guidelines_pass: guidelinesPass, has_banned_words: hasBanned, response_length: response.length, is_within_char_limit: !tooLong };",
          outputVariables: [
            { name: "guidelines_pass", type: "boolean" },
            { name: "has_banned_words", type: "boolean" },
            { name: "response_length", type: "number" },
            { name: "is_within_char_limit", type: "boolean" },
          ],
        },
      }),
      n(
        "5",
        "condition",
        "Is this negative sentiment or needs escalation?",
        960,
        {
          conditionDetails: {
            conditions: [
              { field: "sentiment", operator: "equals", value: "negative" },
            ],
          },
        },
      ),
      slackNode(
        "6",
        "Slack: Alert team of negative or escalation-needed comment",
        1200,
        'ALERT: Negative/escalation social comment detected\\nPlatform: {platform} | Author: @{author_handle}\\nComment: "{comment_text}"\\nSentiment: {sentiment} | Intent: {intent} | Urgency: {urgency_level}\\nSuggested response ({tone_used}): "{suggested_response}"\\nPriority: {response_priority}/5 | Escalation: {escalation_needed}',
      ),
      n("7", "approvalGate", "Social manager: Approve or edit response", 1440, {
        approvalGateDetails: {
          approvers: ["social-media-manager"],
          message:
            'Comment from @{author_handle} ({platform}): "{comment_text}"\\nSuggested response: "{suggested_response}"\\nTone: {tone_used} | Guidelines pass: {guidelines_pass}\\nPlease approve, edit, or decline.',
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log comment triage and response",
        1680,
        "SocialCommentTriage",
        '[["{platform}","{author_handle}","{comment_text}","{sentiment}","{intent}","{urgency_level}","{suggested_response}","{response_priority}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Social comment triage: new comment triggers AI sentiment/intent classification, AI generates a brand-aligned response suggestion, brand guidelines are checked, negative comments alert the team via Slack, social manager approves responses through an approval gate, and all interactions are logged in Sheets.",
  },

  // ── 81. Community Post Auto-Responder | Content | Pattern K ──
  "Community Post Auto-Responder": {
    nodes: [
      trigger("Discourse: New community post webhook", "webhook_app", {
        source: "Discourse",
        event: "post.created",
        howItStarts:
          "Fires when a new post is created in the Discourse community forum.",
        setup:
          "Connect Discourse webhook for post_created events. Payload includes topic title, post body, author username, and category.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Classify topic and extract question intent",
        240,
        "You are a community support classifier. Analyze this Discourse post and classify it. Return JSON with post_title, post_body, author_username, category_name, topic_type (question/discussion/feature-request/bug-report/announcement), and question_keywords (key search terms if it's a question).",
        "Discourse post payload: {{{webhook_body}}}",
        [
          { field: "post_title", as: "post_title", type: "string" },
          { field: "post_body", as: "post_body", type: "string" },
          { field: "author_username", as: "author_username", type: "string" },
          { field: "category_name", as: "category_name", type: "string" },
          { field: "topic_type", as: "topic_type", type: "string" },
          {
            field: "question_keywords",
            as: "question_keywords",
            type: "string",
          },
        ],
      ),
      n(
        "3",
        "hitApi",
        "Pinecone: Search knowledge base for matching answers",
        480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://{pinecone_index}-{pinecone_project}.svc.{pinecone_environment}.pinecone.io/query",
            headers: [
              { key: "Api-Key", value: "{pinecone_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "vector", value: "{question_embedding}" },
              { key: "topK", value: "3" },
              { key: "includeMetadata", value: "true" },
            ],
            variables: [
              {
                name: "matches[0].metadata.content",
                customName: "kb_answer_1",
                type: "string",
              },
              {
                name: "matches[0].score",
                customName: "kb_confidence",
                type: "number",
              },
              {
                name: "matches[0].metadata.article_url",
                customName: "kb_article_url",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "4",
        "OpenAI: Draft community response from KB results",
        720,
        "You are a helpful community moderator. Draft a response to this community post using the knowledge base results. Return JSON with draft_response (helpful, friendly reply), confidence_score (0-100 how confident the answer matches), citations (relevant article links), and needs_human_review (boolean if confidence is below 85).",
        'Post: "{post_title}" by @{author_username}. Body: {post_body}. KB match: {kb_answer_1} (confidence: {kb_confidence}). Article: {kb_article_url}.',
        [
          { field: "draft_response", as: "draft_response", type: "string" },
          { field: "confidence_score", as: "confidence_score", type: "number" },
          { field: "citations", as: "citations", type: "string" },
          {
            field: "needs_human_review",
            as: "needs_human_review",
            type: "boolean",
          },
        ],
      ),
      n("5", "condition", "Is AI confidence above 85%?", 960, {
        conditionDetails: {
          conditions: [
            {
              field: "confidence_score",
              operator: "greater_than",
              value: "85",
            },
          ],
        },
      }),
      n("6", "hitApi", "Discourse API: Post auto-response to thread", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://{discourse_domain}/posts.json",
          headers: [
            { key: "Api-Key", value: "{discourse_api_key}" },
            { key: "Api-Username", value: "system" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "topic_id", value: "{topic_id}" },
            {
              key: "raw",
              value: "{draft_response}\\n\\nReferences: {citations}",
            },
          ],
          variables: [
            { name: "id", customName: "discourse_reply_id", type: "number" },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Flag low-confidence posts for human review",
        1440,
        'Low-confidence community post needs human review:\\nPost: "{post_title}" by @{author_username} in {category_name}\\nType: {topic_type}\\nAI confidence: {confidence_score}%\\nDraft response: "{draft_response}"\\nPlease review and respond manually.',
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log community auto-response activity",
        1680,
        "CommunityResponses",
        '[["{post_title}","{author_username}","{category_name}","{topic_type}","{confidence_score}","{needs_human_review}","{discourse_reply_id}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "8"),
      e("7", "8"),
    ],
    summary:
      "Community auto-responder: Discourse post triggers AI topic classification, Pinecone searches the knowledge base for matching answers, AI drafts a response with confidence scoring, high-confidence replies are auto-posted to the thread, low-confidence posts are flagged in Slack for human review, and all activity is logged in Sheets.",
  },

  // ── 82. Community Moderation Queue | Content | Pattern K ──
  "Community Moderation Queue": {
    nodes: [
      trigger("Webhook: User-generated content submitted", "webhook_app", {
        source: "Community",
        event: "content.submitted",
        howItStarts:
          "Fires when a user submits content (post, comment, or media) to the community platform for moderation.",
        setup:
          "Connect community platform webhook for content submission events. Payload includes content text, author, content type, and any attached media URLs.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Classify toxicity and policy compliance",
        240,
        "You are a content moderation AI. Analyze this user-submitted content for toxicity and policy violations. Return JSON with content_text, author_id, content_type (post/comment/media), toxicity_score (0-100), policy_violations (comma-separated list or 'none'), violation_severity (none/minor/major/critical), and moderation_reason (brief explanation).",
        "User content submission: {{{webhook_body}}}",
        [
          { field: "content_text", as: "content_text", type: "string" },
          { field: "author_id", as: "author_id", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "toxicity_score", as: "toxicity_score", type: "number" },
          {
            field: "policy_violations",
            as: "policy_violations",
            type: "string",
          },
          {
            field: "violation_severity",
            as: "violation_severity",
            type: "string",
          },
          {
            field: "moderation_reason",
            as: "moderation_reason",
            type: "string",
          },
        ],
      ),
      n("3", "runCode", "Apply moderation rules and determine action", 480, {
        runCodeDetails: {
          code: "const score = input.toxicity_score || 0; const severity = input.violation_severity || 'none'; const autoBlock = score > 80 || severity === 'critical'; const autoApprove = score < 20 && severity === 'none'; const needsReview = !autoBlock && !autoApprove; const action = autoBlock ? 'hide' : autoApprove ? 'approve' : 'queue_review'; return { moderation_action: action, auto_blocked: autoBlock, auto_approved: autoApprove, needs_manual_review: needsReview };",
          outputVariables: [
            { name: "moderation_action", type: "string" },
            { name: "auto_blocked", type: "boolean" },
            { name: "auto_approved", type: "boolean" },
            { name: "needs_manual_review", type: "boolean" },
          ],
        },
      }),
      n("4", "condition", "Does content violate community policies?", 720, {
        conditionDetails: {
          conditions: [
            { field: "auto_blocked", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "hitApi", "Community API: Hide violating content", 960, {
        hitApiDetails: {
          method: "PUT",
          url: "https://{community_domain}/api/v1/content/{content_id}/moderate",
          headers: [
            { key: "Authorization", value: "Bearer {community_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "action", value: "hide" },
            { key: "reason", value: "{moderation_reason}" },
          ],
          variables: [
            { name: "status", customName: "moderation_status", type: "string" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Send to moderator review queue",
        1200,
        'Content moderation queue:\\nAuthor: {author_id} | Type: {content_type}\\nContent: "{content_text}"\\nToxicity: {toxicity_score}/100 | Severity: {violation_severity}\\nViolations: {policy_violations}\\nAction taken: {moderation_action}\\nReason: {moderation_reason}',
      ),
      emailNode(
        "7",
        "Email: Notify content author of moderation action",
        1440,
        "{author_email}",
        "Your {content_type} has been reviewed by our moderation team",
        "Hello,\\n\\nYour recently submitted {content_type} has been reviewed by our community moderation system.\\n\\nAction: {moderation_action}\\nReason: {moderation_reason}\\n\\nIf you believe this was an error, please reply to this email or contact our community support team.\\n\\nOur community guidelines help ensure a safe and respectful environment for all members.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log moderation action",
        1680,
        "ModerationLog",
        '[["{author_id}","{content_type}","{toxicity_score}","{violation_severity}","{policy_violations}","{moderation_action}","{moderation_reason}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Community moderation queue: content submission triggers AI toxicity and policy analysis, automated rules determine moderation action, violating content is hidden via API, borderline content is queued for moderator review in Slack, authors are notified by email, and all moderation actions are logged in Sheets.",
  },

  // ── 83. Reddit Mention Monitor | Content | Pattern L ──
  "Reddit Mention Monitor": {
    nodes: [
      trigger("Schedule: Hourly Reddit brand mention scan", "schedule", {
        schedule: "Every hour",
        howItStarts:
          "Runs every hour to scan Reddit for new brand mentions, product discussions, and relevant subreddit activity.",
        setup:
          "Configure hourly schedule. Ensure Reddit API credentials are set up with read access to search endpoints.",
        inputFields: [],
      }),
      n(
        "2",
        "hitApi",
        "Reddit API: Search for brand mentions across subreddits",
        240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://oauth.reddit.com/search?q={brand_name}&sort=new&t=hour&limit=25",
            headers: [
              { key: "Authorization", value: "Bearer {reddit_token}" },
              { key: "User-Agent", value: "{reddit_user_agent}" },
            ],
            body: [],
            variables: [
              {
                name: "data.children.length",
                customName: "mention_count",
                type: "number",
              },
              {
                name: "data.children",
                customName: "mentions_raw",
                type: "string",
              },
            ],
          },
        },
      ),
      aiNode(
        "3",
        "OpenAI: Classify relevance and sentiment of mentions",
        480,
        "You are a social listening analyst. Analyze these Reddit mentions and classify each. Return JSON with relevant_mentions (count of truly relevant mentions), top_mention_title, top_mention_subreddit, top_mention_url, overall_sentiment (positive/neutral/negative), sentiment_breakdown (e.g. '3 positive, 1 neutral, 1 negative'), and key_themes (comma-separated topics).",
        'Reddit search results for brand "{brand_name}": {mentions_raw}. Total mentions: {mention_count}.',
        [
          {
            field: "relevant_mentions",
            as: "relevant_mentions",
            type: "number",
          },
          {
            field: "top_mention_title",
            as: "top_mention_title",
            type: "string",
          },
          {
            field: "top_mention_subreddit",
            as: "top_mention_subreddit",
            type: "string",
          },
          { field: "top_mention_url", as: "top_mention_url", type: "string" },
          {
            field: "overall_sentiment",
            as: "overall_sentiment",
            type: "string",
          },
          {
            field: "sentiment_breakdown",
            as: "sentiment_breakdown",
            type: "string",
          },
          { field: "key_themes", as: "key_themes", type: "string" },
        ],
      ),
      n("4", "runCode", "Deduplicate against previously logged mentions", 720, {
        runCodeDetails: {
          code: "const relevant = input.relevant_mentions || 0; const isNew = relevant > 0; const hasNegative = (input.overall_sentiment || '').toLowerCase() === 'negative'; const needsAlert = isNew && (relevant >= 3 || hasNegative); return { has_new_mentions: isNew, needs_alert: needsAlert, is_negative_trend: hasNegative, deduped_count: relevant };",
          outputVariables: [
            { name: "has_new_mentions", type: "boolean" },
            { name: "needs_alert", type: "boolean" },
            { name: "is_negative_trend", type: "boolean" },
            { name: "deduped_count", type: "number" },
          ],
        },
      }),
      n("5", "condition", "Are there relevant new mentions to report?", 960, {
        conditionDetails: {
          conditions: [
            { field: "has_new_mentions", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Alert social team of Reddit mentions",
        1200,
        'Reddit Brand Mentions ({deduped_count} relevant this hour)\\nSentiment: {overall_sentiment} ({sentiment_breakdown})\\nTop mention: "{top_mention_title}" in r/{top_mention_subreddit}\\nLink: {top_mention_url}\\nThemes: {key_themes}\\nNegative trend: {is_negative_trend}',
      ),
      n("7", "hitApi", "Airtable: Log Reddit mentions for tracking", 1440, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.airtable.com/v0/{airtable_base_id}/RedditMentions",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "records",
              value:
                '[{"fields":{"Scan Time":"=NOW()","Relevant Mentions":{deduped_count},"Sentiment":"{overall_sentiment}","Top Mention":"{top_mention_title}","Subreddit":"{top_mention_subreddit}","URL":"{top_mention_url}","Themes":"{key_themes}"}}]',
            },
          ],
          variables: [
            {
              name: "records[0].id",
              customName: "airtable_mention_id",
              type: "string",
            },
          ],
        },
      }),
      emailNode(
        "8",
        "Email: Send Reddit mention digest to team",
        1680,
        "{social_team_email}",
        "Reddit Mention Digest — {deduped_count} new mentions detected",
        'Reddit Brand Monitoring Report\\n\\nRelevant mentions this hour: {deduped_count}\\nOverall sentiment: {overall_sentiment}\\nBreakdown: {sentiment_breakdown}\\n\\nTop mention: "{top_mention_title}"\\nSubreddit: r/{top_mention_subreddit}\\nLink: {top_mention_url}\\n\\nKey themes: {key_themes}\\n\\nNegative trend detected: {is_negative_trend}\\n\\nReview the Airtable log for full details.',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Reddit mention monitoring: hourly schedule triggers Reddit API search for brand mentions, AI classifies relevance and sentiment, mentions are deduplicated, relevant mentions alert the social team via Slack, Airtable logs all mentions for tracking, and an email digest summarizes the findings.",
  },

  // ── 84. X Viral Idea Hunter | Content | Pattern L ──
  "X Viral Idea Hunter": {
    nodes: [
      trigger("Schedule: Daily trending topic scan on X", "schedule", {
        schedule: "Every day at 8:00 AM",
        howItStarts:
          "Runs daily to scan X (Twitter) trending topics and identify brand-relevant viral content opportunities.",
        setup:
          "Configure daily schedule at 8AM. Ensure X API v2 credentials are configured with read access to trends and search endpoints.",
        inputFields: [],
      }),
      n("2", "hitApi", "X API: Fetch trending topics and viral posts", 240, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.twitter.com/2/trends/by/woeid/1?tweet.fields=public_metrics",
          headers: [{ key: "Authorization", value: "Bearer {x_bearer_token}" }],
          body: [],
          variables: [
            { name: "data", customName: "trending_topics_raw", type: "string" },
            { name: "data.length", customName: "topic_count", type: "number" },
          ],
        },
      }),
      aiNode(
        "3",
        "OpenAI: Filter for brand-relevant viral trends",
        480,
        "You are a social media trend analyst. Filter these trending topics for brand relevance. Return JSON with relevant_trends (count), top_trend_name, top_trend_volume, brand_angle (how the brand can participate), content_idea (specific post idea), trend_category (industry/culture/tech/meme/news), and urgency (high/medium/low based on trend velocity).",
        "Trending topics on X: {trending_topics_raw}. Brand context: {brand_description}. Industry: {brand_industry}.",
        [
          { field: "relevant_trends", as: "relevant_trends", type: "number" },
          { field: "top_trend_name", as: "top_trend_name", type: "string" },
          { field: "top_trend_volume", as: "top_trend_volume", type: "string" },
          { field: "brand_angle", as: "brand_angle", type: "string" },
          { field: "content_idea", as: "content_idea", type: "string" },
          { field: "trend_category", as: "trend_category", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),
      n("4", "runCode", "Score virality potential for each trend", 720, {
        runCodeDetails: {
          code: "const relevant = input.relevant_trends || 0; const urgencyScore = input.urgency === 'high' ? 90 : input.urgency === 'medium' ? 60 : 30; const hasAngle = (input.brand_angle || '').length > 10; const viralityScore = Math.min(100, urgencyScore + (hasAngle ? 20 : 0) + (relevant > 3 ? 10 : 0)); const isHighRelevance = viralityScore >= 70; return { virality_score: viralityScore, is_high_relevance: isHighRelevance, has_brand_angle: hasAngle, relevant_trend_count: relevant };",
          outputVariables: [
            { name: "virality_score", type: "number" },
            { name: "is_high_relevance", type: "boolean" },
            { name: "has_brand_angle", type: "boolean" },
            { name: "relevant_trend_count", type: "number" },
          ],
        },
      }),
      n("5", "condition", "Is there a high-relevance trend opportunity?", 960, {
        conditionDetails: {
          conditions: [
            { field: "is_high_relevance", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "6",
        "Slack: Post trend brief to content team",
        1200,
        "VIRAL TREND OPPORTUNITY (Score: {virality_score}/100)\\nTrend: {top_trend_name} | Volume: {top_trend_volume}\\nCategory: {trend_category} | Urgency: {urgency}\\nBrand angle: {brand_angle}\\nContent idea: {content_idea}\\nRelevant trends found: {relevant_trend_count}",
      ),
      n("7", "hitApi", "Airtable: Add to content ideas backlog", 1440, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.airtable.com/v0/{airtable_base_id}/ContentIdeas",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "records",
              value:
                '[{"fields":{"Trend":"{top_trend_name}","Category":"{trend_category}","Virality Score":{virality_score},"Brand Angle":"{brand_angle}","Content Idea":"{content_idea}","Urgency":"{urgency}","Status":"New"}}]',
            },
          ],
          variables: [
            {
              name: "records[0].id",
              customName: "airtable_idea_id",
              type: "string",
            },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Log daily trend scan results",
        1680,
        "TrendScans",
        '[["{top_trend_name}","{trend_category}","{top_trend_volume}","{virality_score}","{brand_angle}","{content_idea}","{urgency}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "8", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Viral trend hunter: daily schedule scans X trending topics via API, AI filters for brand-relevant trends, virality potential is scored, high-relevance trends are posted to Slack as content briefs, ideas are added to the Airtable backlog, and daily scan results are logged in Sheets.",
  },

  // ── 85. Creator Content Brief Generator | Content | Pattern I ──
  "Creator Content Brief Generator": {
    nodes: [
      trigger("Webhook: Creator brief request submitted", "webhook_form", {
        source: "CreatorTeam",
        event: "brief.requested",
        howItStarts:
          "Fires when a content creator or influencer submits a brief request through the intake form.",
        setup:
          "Connect the creator brief request form webhook. Payload includes creator name, content type, topic area, target audience, and deliverable requirements.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Research topic and audience insights",
        240,
        "You are a content strategy researcher. Analyze the creator brief request and research the topic. Return JSON with creator_name, content_type (blog/video/social/podcast), topic_area, target_audience, audience_pain_points (3 points joined by |), trending_angles (comma-separated), and competitive_gap (what competitors are missing).",
        "Creator brief request: {{{webhook_body}}}",
        [
          { field: "creator_name", as: "creator_name", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          { field: "topic_area", as: "topic_area", type: "string" },
          { field: "target_audience", as: "target_audience", type: "string" },
          {
            field: "audience_pain_points",
            as: "audience_pain_points",
            type: "string",
          },
          { field: "trending_angles", as: "trending_angles", type: "string" },
          { field: "competitive_gap", as: "competitive_gap", type: "string" },
        ],
      ),
      aiNode(
        "3",
        "OpenAI: Generate detailed content brief",
        480,
        "You are a content brief specialist. Create a comprehensive content brief for a creator. Return JSON with brief_title, content_outline (structured outline with sections joined by |||), key_messages (3 brand messages to weave in, joined by |), suggested_cta, tone_guidelines, word_count_target, and seo_keywords (comma-separated).",
        "Creator: {creator_name}. Type: {content_type}. Topic: {topic_area}. Audience: {target_audience}. Pain points: {audience_pain_points}. Trending: {trending_angles}. Gap: {competitive_gap}.",
        [
          { field: "brief_title", as: "brief_title", type: "string" },
          { field: "content_outline", as: "content_outline", type: "string" },
          { field: "key_messages", as: "key_messages", type: "string" },
          { field: "suggested_cta", as: "suggested_cta", type: "string" },
          { field: "tone_guidelines", as: "tone_guidelines", type: "string" },
          {
            field: "word_count_target",
            as: "word_count_target",
            type: "string",
          },
          { field: "seo_keywords", as: "seo_keywords", type: "string" },
        ],
      ),
      n("4", "runCode", "Add brand guidelines and format brief document", 720, {
        runCodeDetails: {
          code: "const sections = (input.content_outline || '').split('|||'); const outlineHtml = sections.map((s, i) => '<h3>Section ' + (i+1) + '</h3><p>' + s.trim() + '</p>').join(''); const messages = (input.key_messages || '').split('|').map(m => '<li>' + m.trim() + '</li>').join(''); const briefDoc = '<h1>' + input.brief_title + '</h1><p><strong>Creator:</strong> ' + input.creator_name + ' | <strong>Type:</strong> ' + input.content_type + '</p><p><strong>Target audience:</strong> ' + input.target_audience + '</p><h2>Content Outline</h2>' + outlineHtml + '<h2>Key Brand Messages</h2><ul>' + messages + '</ul><p><strong>CTA:</strong> ' + input.suggested_cta + '</p><p><strong>Tone:</strong> ' + input.tone_guidelines + '</p><p><strong>Target length:</strong> ' + input.word_count_target + '</p><p><strong>SEO keywords:</strong> ' + input.seo_keywords + '</p>'; return { brief_html: briefDoc, section_count: sections.length, brief_ready: true };",
          outputVariables: [
            { name: "brief_html", type: "string" },
            { name: "section_count", type: "number" },
            { name: "brief_ready", type: "boolean" },
          ],
        },
      }),
      n("5", "approvalGate", "Content lead: Approve creator brief", 960, {
        approvalGateDetails: {
          approvers: ["content-lead"],
          message:
            'Creator brief for {creator_name} ready for review:\\nTitle: "{brief_title}"\\nType: {content_type} | Topic: {topic_area}\\nSections: {section_count} | Target: {word_count_target}\\nCTA: {suggested_cta}\\nPlease approve or request changes before sending to creator.',
        },
      }),
      emailNode(
        "6",
        "Email: Send approved brief to creator",
        1200,
        "{creator_email}",
        "Your content brief is ready: {brief_title}",
        'Hi {creator_name},\\n\\nYour content brief is ready for "{brief_title}"!\\n\\nContent type: {content_type}\\nTarget audience: {target_audience}\\nTarget length: {word_count_target}\\n\\nOutline sections: {section_count}\\nSEO keywords: {seo_keywords}\\nTone: {tone_guidelines}\\nCTA: {suggested_cta}\\n\\nKey messages to include:\\n{key_messages}\\n\\nPlease review the full brief document attached. Let us know if you have any questions or need clarification on any section.',
      ),
      slackNode(
        "7",
        "Slack: Notify team of brief sent to creator",
        1440,
        'Creator brief sent to {creator_name}:\\nTitle: "{brief_title}" | Type: {content_type}\\nTopic: {topic_area} | Sections: {section_count}\\nKeywords: {seo_keywords}\\nTarget: {word_count_target}',
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log creator brief assignment",
        1680,
        "CreatorBriefs",
        '[["{creator_name}","{brief_title}","{content_type}","{topic_area}","{word_count_target}","{section_count}","{seo_keywords}","Sent","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Creator brief generator: request form triggers AI topic and audience research, AI generates a detailed content brief with outline and brand messages, brief is formatted with brand guidelines, content lead approves, brief is emailed to the creator, team is notified via Slack, and Sheets tracks the assignment.",
  },

  // ── 86. Asset Request to Creative Queue | Content | Pattern K ──
  "Asset Request to Creative Queue": {
    nodes: [
      trigger("Slack: Asset request message webhook", "webhook_app", {
        source: "Slack",
        event: "message.asset_request",
        howItStarts:
          "Fires when a team member sends an asset request message in the designated Slack channel.",
        setup:
          "Connect Slack webhook for messages in the #asset-requests channel. Use a specific emoji reaction or keyword trigger to identify asset requests.",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Extract asset requirements from request",
        240,
        "You are a creative project coordinator. Extract asset requirements from the Slack message. Return JSON with requester_name, requester_channel, asset_type (image/video/document/presentation/graphic), description, dimensions (if mentioned), deadline, brand (if specified), and urgency (low/medium/high).",
        "Slack asset request message: {{{webhook_body}}}",
        [
          { field: "requester_name", as: "requester_name", type: "string" },
          {
            field: "requester_channel",
            as: "requester_channel",
            type: "string",
          },
          { field: "asset_type", as: "asset_type", type: "string" },
          { field: "description", as: "asset_description", type: "string" },
          { field: "dimensions", as: "dimensions", type: "string" },
          { field: "deadline", as: "deadline", type: "string" },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),
      n("3", "runCode", "Check existing asset library for matches", 480, {
        runCodeDetails: {
          code: "const assetType = input.asset_type || ''; const description = input.asset_description || ''; const searchTerms = description.toLowerCase().split(' ').filter(w => w.length > 3); const assetExists = false; const suggestedAssetUrl = ''; return { asset_exists: assetExists, suggested_asset_url: suggestedAssetUrl, search_terms: searchTerms.join(', '), needs_creation: !assetExists, urgency_priority: input.urgency === 'high' ? 1 : input.urgency === 'medium' ? 2 : 3 };",
          outputVariables: [
            { name: "asset_exists", type: "boolean" },
            { name: "suggested_asset_url", type: "string" },
            { name: "needs_creation", type: "boolean" },
            { name: "urgency_priority", type: "number" },
          ],
        },
      }),
      n("4", "condition", "Does a matching asset already exist?", 720, {
        conditionDetails: {
          conditions: [
            { field: "asset_exists", operator: "equals", value: "true" },
          ],
        },
      }),
      slackNode(
        "5",
        "Slack: Reply with existing asset link",
        960,
        "Hi {requester_name}! Found an existing asset that matches your request:\\n{suggested_asset_url}\\nLet us know if this works or if you need a custom version.",
      ),
      n("6", "hitApi", "Asana: Create creative task in design queue", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Asset Request: {asset_type} — {asset_description}","projects":["{asana_design_project_id}"],"notes":"Requester: {requester_name}\\nType: {asset_type}\\nDimensions: {dimensions}\\nDescription: {asset_description}\\nDeadline: {deadline}\\nUrgency: {urgency}","due_on":"{deadline}","custom_fields":{"{priority_field_id}":"{urgency_priority}"}}',
            },
          ],
          variables: [
            { name: "data.gid", customName: "asana_task_id", type: "string" },
            {
              name: "data.permalink_url",
              customName: "asana_task_url",
              type: "string",
            },
          ],
        },
      }),
      emailNode(
        "7",
        "Email: Send requestor ETA and task link",
        1440,
        "{requester_email}",
        "Your asset request has been queued — {asset_type}",
        "Hi {requester_name},\\n\\nYour asset request has been added to the creative queue:\\n\\nAsset type: {asset_type}\\nDescription: {asset_description}\\nDimensions: {dimensions}\\nDeadline: {deadline}\\nPriority: {urgency}\\n\\nAsana task: {asana_task_url}\\n\\nOur design team will begin work shortly. You'll be notified when the asset is ready for review.",
      ),
      sheetsNode(
        "8",
        "Google Sheets: Log asset request",
        1680,
        "AssetRequests",
        '[["{requester_name}","{asset_type}","{asset_description}","{dimensions}","{deadline}","{urgency}","{asana_task_id}","{needs_creation}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "8"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Asset request pipeline: Slack request triggers AI extraction of requirements, existing asset library is checked for matches, matching assets are returned immediately via Slack, new requests create an Asana task in the design queue, requestor receives an email with ETA and task link, and all requests are logged in Sheets.",
  },

  // ── 87. Image Approval Workflow | Content | Pattern I ──
  "Image Approval Workflow": {
    nodes: [
      trigger("Google Drive: New image uploaded webhook", "webhook_app", {
        source: "GoogleDrive",
        event: "file.created",
        howItStarts:
          "Fires when a new image file is uploaded to the designated brand assets folder in Google Drive.",
        setup:
          "Connect Google Drive webhook for file creation events in the brand assets folder. Filter for image file types (PNG, JPG, SVG, WebP).",
        inputFields: [],
      }),
      aiNode(
        "2",
        "OpenAI: Check brand compliance of uploaded image",
        240,
        "You are a brand compliance analyst. Analyze the image metadata and filename for brand guideline adherence. Return JSON with file_name, file_type, uploader_email, folder_path, brand_compliance_score (0-100), compliance_issues (comma-separated or 'none'), color_palette_match (boolean), and logo_usage_correct (boolean).",
        "Google Drive image upload event: {{{webhook_body}}}",
        [
          { field: "file_name", as: "file_name", type: "string" },
          { field: "file_type", as: "file_type", type: "string" },
          { field: "uploader_email", as: "uploader_email", type: "string" },
          {
            field: "brand_compliance_score",
            as: "brand_compliance_score",
            type: "number",
          },
          {
            field: "compliance_issues",
            as: "compliance_issues",
            type: "string",
          },
          {
            field: "color_palette_match",
            as: "color_palette_match",
            type: "boolean",
          },
          {
            field: "logo_usage_correct",
            as: "logo_usage_correct",
            type: "boolean",
          },
        ],
      ),
      n("3", "runCode", "Validate image dimensions and file specs", 480, {
        runCodeDetails: {
          code: "const fileName = input.file_name || ''; const fileType = (input.file_type || '').toLowerCase(); const validTypes = ['png', 'jpg', 'jpeg', 'svg', 'webp']; const typeValid = validTypes.some(t => fileType.includes(t)); const complianceOk = (input.brand_compliance_score || 0) >= 80; const meetsSpec = typeValid && complianceOk; return { type_valid: typeValid, compliance_ok: complianceOk, meets_spec: meetsSpec, spec_summary: 'Type: ' + (typeValid ? 'OK' : 'FAIL') + ' | Compliance: ' + input.brand_compliance_score + '/100' };",
          outputVariables: [
            { name: "type_valid", type: "boolean" },
            { name: "compliance_ok", type: "boolean" },
            { name: "meets_spec", type: "boolean" },
            { name: "spec_summary", type: "string" },
          ],
        },
      }),
      n("4", "condition", "Does the image meet brand specifications?", 720, {
        conditionDetails: {
          conditions: [
            { field: "meets_spec", operator: "equals", value: "true" },
          ],
        },
      }),
      n("5", "approvalGate", "Creative director: Final image approval", 960, {
        approvalGateDetails: {
          approvers: ["creative-director"],
          message:
            "Image uploaded for approval: {file_name}\\nUploader: {uploader_email}\\nType: {file_type} | {spec_summary}\\nBrand compliance: {brand_compliance_score}/100\\nIssues: {compliance_issues}\\nColor match: {color_palette_match} | Logo correct: {logo_usage_correct}\\nPlease approve or reject with feedback.",
        },
      }),
      n("6", "hitApi", "Airtable: Update image status to approved", 1200, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.airtable.com/v0/{airtable_base_id}/BrandAssets",
          headers: [
            { key: "Authorization", value: "Bearer {airtable_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "records",
              value:
                '[{"fields":{"File Name":"{file_name}","File Type":"{file_type}","Uploader":"{uploader_email}","Compliance Score":{brand_compliance_score},"Issues":"{compliance_issues}","Status":"Approved"}}]',
            },
          ],
          variables: [
            {
              name: "records[0].id",
              customName: "airtable_asset_id",
              type: "string",
            },
          ],
        },
      }),
      slackNode(
        "7",
        "Slack: Notify team of approved image asset",
        1440,
        "Image asset approved: {file_name}\\nUploader: {uploader_email} | Type: {file_type}\\nBrand compliance: {brand_compliance_score}/100\\nAirtable: {airtable_asset_id}\\nThe asset is now available in the brand library.",
      ),
      emailNode(
        "8",
        "Email: Send approval confirmation to uploader",
        1680,
        "{uploader_email}",
        "Your image has been approved: {file_name}",
        'Hi,\\n\\nYour uploaded image "{file_name}" has been reviewed and approved by the creative team.\\n\\nBrand compliance score: {brand_compliance_score}/100\\nFile type: {file_type}\\n\\nThe asset is now available in the brand asset library for team use.\\n\\nIf you have additional assets to upload, please use the same brand assets folder in Google Drive.',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "8", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Image approval workflow: Google Drive upload triggers AI brand compliance check, image dimensions and specs are validated, compliant images go to creative director for final approval, approved assets are logged in Airtable, team is notified via Slack, and the uploader receives an email confirmation.",
  },

  // ── 88. UGC Rights Request Automation | Content | Pattern J ──
  "UGC Rights Request Automation": {
    nodes: [
      trigger(
        "Webhook: User-generated content identified for use",
        "webhook_app",
        {
          source: "SocialListening",
          event: "ugc.identified",
          howItStarts:
            "Fires when a piece of user-generated content is identified and tagged for potential brand use.",
          setup:
            "Connect the UGC identification webhook. Payload includes content URL, creator handle, platform, content type, and the team member who flagged it.",
          inputFields: [],
        },
      ),
      aiNode(
        "2",
        "OpenAI: Draft personalized rights request message",
        240,
        "You are a UGC rights management specialist. Draft a professional, friendly rights request message. Return JSON with creator_handle, creator_platform, content_url, content_type (photo/video/testimonial/review), rights_request_message (personalized outreach message requesting permission), usage_scope (where the brand intends to use it), and flagged_by (team member).",
        "UGC identification payload: {{{webhook_body}}}",
        [
          { field: "creator_handle", as: "creator_handle", type: "string" },
          { field: "creator_platform", as: "creator_platform", type: "string" },
          { field: "content_url", as: "content_url", type: "string" },
          { field: "content_type", as: "content_type", type: "string" },
          {
            field: "rights_request_message",
            as: "rights_request_message",
            type: "string",
          },
          { field: "usage_scope", as: "usage_scope", type: "string" },
          { field: "flagged_by", as: "flagged_by", type: "string" },
        ],
      ),
      n("3", "runCode", "Check prior consent database for creator", 480, {
        runCodeDetails: {
          code: "const handle = input.creator_handle || ''; const platform = input.creator_platform || ''; const priorConsent = false; const recentRequest = false; const daysSinceLastRequest = 999; const canRequest = !recentRequest && daysSinceLastRequest > 30; return { has_prior_consent: priorConsent, can_send_request: canRequest, days_since_last_request: daysSinceLastRequest, already_consented: priorConsent };",
          outputVariables: [
            { name: "has_prior_consent", type: "boolean" },
            { name: "can_send_request", type: "boolean" },
            { name: "days_since_last_request", type: "number" },
            { name: "already_consented", type: "boolean" },
          ],
        },
      }),
      n("4", "condition", "Has creator already given consent?", 720, {
        conditionDetails: {
          conditions: [
            { field: "already_consented", operator: "equals", value: "true" },
          ],
        },
      }),
      emailNode(
        "5",
        "Email: Send rights request to content creator",
        960,
        "{creator_email}",
        "We love your content, @{creator_handle}! Can we share it?",
        "Hi @{creator_handle},\\n\\nWe spotted your amazing {content_type} on {creator_platform} and we'd love to feature it!\\n\\nContent: {content_url}\\n\\n{rights_request_message}\\n\\nUsage scope: {usage_scope}\\n\\nIf you're interested, simply reply to this email with 'I approve' and we'll take care of the rest. We'll always credit you and link back to your original post.\\n\\nThank you for creating such great content!",
      ),
      n("6", "delayAction", "Wait 5 days for creator response", 1200, {
        delayDetails: { duration: 5, unit: "days" },
      }),
      n("7", "condition", "Has consent been received from creator?", 1440, {
        conditionDetails: {
          conditions: [
            { field: "consent_received", operator: "equals", value: "true" },
          ],
        },
      }),
      sheetsNode(
        "8",
        "Google Sheets: Update UGC rights status",
        1680,
        "UGCRightsTracker",
        '[["{creator_handle}","{creator_platform}","{content_type}","{content_url}","{usage_scope}","{flagged_by}","{consent_received}","=NOW()"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "8", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
      e("7", "8", "output_2"),
    ],
    summary:
      "UGC rights automation: identified user content triggers AI to draft a personalized rights request, prior consent database is checked, new requests are emailed to the creator, 5-day waiting period allows for response, consent status is tracked, and all rights requests are logged in Sheets for compliance tracking.",
  },
};
